import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { identityServices } from '../identityServices'
import { UpdateProfilePayload } from '../identityTypes'
import { QUERY_KEYS } from '@/constants/queryKeys'

interface UseUpdateProfileOptions {
  onSuccess?: (message: string) => void
  onError?: (error: { errorMessage?: string }) => void
}

export function useUpdateProfile(options?: UseUpdateProfileOptions) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data: UpdateProfilePayload) =>
      identityServices.updateProfile(data),
    onSuccess: () => {
      // Refresh user profile cache (same as React Native version)
      queryClient.refetchQueries({ queryKey: [QUERY_KEYS.IDENTITY_ME] })

      const successMessage = t('form:success.changesSaved')
      toast.success(successMessage)

      if (options?.onSuccess) {
        options.onSuccess(successMessage)
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.message || t('api:errors.somethingWrong')
      toast.error(errorMessage)

      if (options?.onError) {
        options.onError({ errorMessage })
      }
    },
  })

  async function updateProfile(data: UpdateProfilePayload) {
    mutation.mutate(data)
  }

  return {
    updateProfile,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
  }
}
